/**
 * Where the Label Properties panel gets its data.
 *
 * Field discovery differs per service, so it lives here rather than in the
 * panel: the panel asks for "the fields of this layer" and does not care
 * whether the answer came from an ArcGIS metadata document, a WFS schema, or
 * the features already in memory.
 */

import type { AppModule, Layer } from '../types';
import { arcgisServiceUrlFor, getArcgisBaseUrl, withArcgisToken } from '../utils/arcgis';
import { getLayerCapabilities, type LabelingMode } from '../layers/capabilities';
import { fetchWfsFields } from '../layers/sld';

export interface LabelField {
  name: string;
  alias: string;
}

/**
 * ArcGIS field types worth labelling.
 *
 * Geometry, blob and raster columns are excluded because there is nothing
 * meaningful to print; OIDs are kept, since labelling by feature id is a common
 * way to cross-reference a printed map.
 */
const LABELLABLE_ESRI_TYPES = new Set([
  'esriFieldTypeString',
  'esriFieldTypeSmallInteger',
  'esriFieldTypeInteger',
  'esriFieldTypeSingle',
  'esriFieldTypeDouble',
  'esriFieldTypeDate',
  'esriFieldTypeOID',
  'esriFieldTypeGUID',
  'esriFieldTypeGlobalID',
]);

export function getLabelingMode(layer: Layer, module: AppModule): LabelingMode {
  return getLayerCapabilities(layer, module).labelingMode;
}

/** One sentence explaining how this layer's labels actually reach the map. */
export function describeLabelingMode(mode: LabelingMode): string {
  switch (mode) {
    case 'client':
      return 'Labels are drawn in the browser from this layer’s own attribute values.';
    case 'sld':
      return 'Labels are drawn by GeoServer and returned as a transparent overlay, so the layer’s existing WMS styling is unchanged.';
    default:
      return 'This layer has no supported labelling mechanism.';
  }
}

/**
 * Field list for the label dropdown.
 *
 * Never throws: a service that refuses to describe itself should leave the
 * panel usable (the user can still type an expression), not break it.
 */
export async function fetchLabelFields(layer: Layer, module: AppModule): Promise<LabelField[]> {
  try {
    // A file-backed layer carries its features, so the schema is right there.
    if (layer.localData) {
      const names = new Set<string>();
      for (const feature of layer.localData.features ?? []) {
        for (const key of Object.keys(feature.properties ?? {})) {
          // Internal keys the importer adds are not user data.
          if (!key.startsWith('_')) names.add(key);
        }
      }
      return Array.from(names).map((name) => ({ name, alias: name }));
    }

    if (layer.arcgisLayerId === undefined) {
      if (!module.geoServerUrl) return [];
      return await fetchWfsFields(module.geoServerUrl, layer.name);
    }

    const effectiveUrl = arcgisServiceUrlFor(layer, module);
    const baseUrl = getArcgisBaseUrl(effectiveUrl);
    if (!baseUrl) return [];

    const response = await fetch(withArcgisToken(`${baseUrl}/${layer.arcgisLayerId}?f=json`, effectiveUrl));
    if (!response.ok) return [];
    const data = await response.json();
    if (data?.error) return [];

    return (data.fields ?? [])
      .filter((field: any) => LABELLABLE_ESRI_TYPES.has(field?.type))
      .map((field: any) => ({ name: field.name, alias: field.alias || field.name }));
  } catch (error) {
    console.error(`[labeling] field list failed layer="${layer.name}"`, error);
    return [];
  }
}
